import React, {useEffect, useState} from 'react'
import {Box} from '@mui/material'
import dayjs from 'dayjs'
import AppDialog from 'src/components-new/feedback/AppDialog'
import Button from 'src/components-new/primitives/Button'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import DatePickerNew from 'src/components-new/inputs/DatePickerNew'
import TimePickerNew from 'src/components-new/inputs/TimePickerNew'
import Card from 'src/components-new/layout/Card'
import DataTable from 'src/components-new/data/DataTable'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import {useAppSelector} from 'src/hooks'
import {STATUS} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'
import {buildParticipantColumns, ParticipantRow} from 'src/pages-new/agenda/virtualMeetingsColumns'

// New-style port of src/components/elements/VirtualMeetingsDetail/VirtualMeetingDetail.tsx
// for use inside the patient-detail "Activities" tab (opened when a row with
// type === 'VIRTUAL_MEETING' is clicked).

interface VirtualMeetingDetailDialogNewProps {
  open: boolean
  event?: Record<string, any>
  onClose: () => void
}

function toParticipantRows(participants: Record<string, any>[] | undefined): ParticipantRow[] {
  return (participants ?? []).map((participant, index) => ({
    key: participant._id ?? participant.id ?? String(index),
    firstName: participant.firstName,
    lastName: participant.lastName,
    email: participant.email,
    qualification: participant.qualification,
  }))
}

/**
 * Merges a calendar day (year/month/date taken from `day`) with a clock time
 * (hour/minute taken from `time`) into a single ISO-8601 string. Mirrors the
 * legacy pattern: values.startDate.set({year, month, date: values.startDay...}).toISOString()
 */
function mergeDateAndTime(day: string | null, time: string | null): string | undefined {
  if (!day || !time) return undefined
  const dayjsDay = dayjs(day)
  const dayjsTime = dayjs(time)
  if (!dayjsDay.isValid() || !dayjsTime.isValid()) return undefined
  return dayjsTime
    .set('year', dayjsDay.year())
    .set('month', dayjsDay.month())
    .set('date', dayjsDay.date())
    .toISOString()
}

export default function VirtualMeetingDetailDialogNew({
  open,
  event,
  onClose,
}: VirtualMeetingDetailDialogNewProps): JSX.Element {
  const toast = useToast()
  const loggedUser = useAppSelector(({userReducer}) => userReducer.data)
  const {detailColumnOptions} = useAppSelector(
    (state) => state.pspConfigReducer.data.virtualMeetingsConfigurations
  )
  const participantColumns = buildParticipantColumns(detailColumnOptions)

  const [title, setTitle] = useState('')
  const [startDay, setStartDay] = useState<string | null>(null)
  const [startTime, setStartTime] = useState<string | null>(null)
  const [endTime, setEndTime] = useState<string | null>(null)
  const [startDayError, setStartDayError] = useState<string | undefined>()
  const [submitted, setSubmitted] = useState(false)
  const [status, setStatus] = useState<string | undefined>()

  const isReadOnlyMode = event?.organizer?._id !== loggedUser?._id || (event?.startDate ?? '') < dayjs().toISOString()

  useEffect(() => {
    if (!open) return
    setTitle(event?.title ?? '')
    setStartDay(event?.startDate ?? null)
    setStartTime(event?.startDate ?? null)
    setEndTime(event?.endDate ?? null)
    setStartDayError(undefined)
    setSubmitted(false)
    setStatus(undefined)
  }, [open, event])

  const validateStartDay = (value: string | null): string | undefined => {
    if (!value) return 'Campo obbligatorio'
    if (dayjs(value).isBefore(dayjs())) return "L'appuntamento non può essere nel passato"
    return undefined
  }

  const handleStartDayChange = (value: string | null): void => {
    setStartDay(value)
    setStartDayError(validateStartDay(value))
  }

  const handleCopyLink = async (): Promise<void> => {
    if (!event?.linkTeleconsultation) return
    await navigator.clipboard.writeText(event.linkTeleconsultation)
    toast.success('Link copiato negli appunti')
  }

  const handleOpenLink = (): void => {
    if (!event?.linkTeleconsultation) return
    window.open(event.linkTeleconsultation, '_blank', 'noopener,noreferrer')
  }

  const handleSave = async (): Promise<void> => {
    setSubmitted(true)
    const dayError = validateStartDay(startDay)
    setStartDayError(dayError)
    if (!event?._id || dayError) return

    const mergedStartDate = mergeDateAndTime(startDay, startTime)
    const mergedEndDate = mergeDateAndTime(startDay, endTime)

    let finalStatus: string | undefined
    let errorStatusCode: number | undefined
    const captureStatus = (nextStatus: string | undefined): void => {
      finalStatus = nextStatus
      setStatus(nextStatus)
    }
    await apiClient.patchVirtualMeetings(event._id, {
      title,
      startDate: mergedStartDate,
      endDate: mergedEndDate,
    }, captureStatus, (_message, statusCode) => {
      errorStatusCode = statusCode
    })

    if (finalStatus === STATUS.FULFILLED) {
      toast.success('Modificati i dati con successo')
    } else if (errorStatusCode === 403) {
      toast.error('Per modificare questa attività contatta la Centrale Operativa di Italiassistenza')
    } else if (finalStatus === STATUS.REJECTED) {
      toast.error('Errore nel modificare i dati della pagina.')
    }
  }

  const organizerName = event?.organizer
    ? `${event.organizer.firstName ?? ''} ${event.organizer.lastName ?? ''}`.trim()
    : ''

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="Incontro virtuale"
      maxWidth="md"
      actions={(
        <Button
          variant="primary"
          disabled={isReadOnlyMode}
          loading={status === STATUS.PENDING}
          onClick={handleSave}
        >
          Salva
        </Button>
      )}
    >
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
        <Card>
          <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'}, gap: 2}}>
            <TextFieldNew
              label="Titolo"
              value={title}
              onChange={setTitle}
              disabled={isReadOnlyMode}
              fullWidth
            />
            <TextFieldNew
              label="Codice"
              value={event?._id ?? ''}
              onChange={() => undefined}
              disabled
              fullWidth
            />
            <DatePickerNew
              label="Data evento"
              value={startDay}
              onChange={handleStartDayChange}
              disabled={isReadOnlyMode}
              required
              error={submitted && Boolean(startDayError)}
              helperText={submitted ? startDayError : undefined}
              fullWidth
            />
            <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2}}>
              <TimePickerNew
                label="Ora inizio"
                value={startTime}
                onChange={setStartTime}
                disabled={isReadOnlyMode}
                fullWidth
              />
              <TimePickerNew
                label="Ora fine"
                value={endTime}
                onChange={setEndTime}
                disabled={isReadOnlyMode}
                fullWidth
              />
            </Box>
            <Box sx={{gridColumn: '1 / -1', display: 'flex', alignItems: 'flex-end', gap: 1}}>
              <Box sx={{flexGrow: 1, minWidth: 0}}>
                <TextFieldNew
                  label="Link per il collegamento"
                  value={event?.linkTeleconsultation ?? ''}
                  onChange={() => undefined}
                  disabled
                  fullWidth
                />
              </Box>
              <ActionIconButton
                variant="videocam"
                tooltip="Apri il link del videoconsulto"
                onClick={handleOpenLink}
                disabled={!event?.linkTeleconsultation}
              />
              <ActionIconButton
                variant="copy"
                tooltip="Copia il link"
                onClick={handleCopyLink}
                disabled={!event?.linkTeleconsultation}
              />
            </Box>
            <TextFieldNew
              label="Organizzatore"
              value={organizerName}
              onChange={() => undefined}
              disabled
              fullWidth
              sx={{gridColumn: '1 / -1'}}
            />
          </Box>
        </Card>

        <Card title="Partecipanti">
          <DataTable<ParticipantRow>
            columns={participantColumns}
            rows={toParticipantRows(event?.participants)}
            keyField="key"
            emptyMessage="Nessun partecipante"
          />
        </Card>
      </Box>
    </AppDialog>
  )
}
